/**
 * New console — showing a lead's timestamps.
 *
 * Every lead time in this product belongs to a clinic, not to whoever is looking at it.
 * `created_at` is stored Pacific and each clinic row carries its own `timezone`, so a
 * bare `moment(str)` renders in the *viewer's* zone: the same lead reads 09:14 to the
 * clinic and 21:44 to somebody in India, and the two consoles then disagree about when
 * a call came in. The classic has always formatted through the clinic zone; these
 * wrappers are that rule in one place so a screen cannot quietly skip it.
 *
 * The explicit parse format matters as much as the zone. `moment.tz(str, tz)` on an
 * ISO string with an offset re-reads the wall time rather than converting the instant;
 * `'YYYY-MM-DDTHH:mm:ssZ'` is the format the classic parses with and what the API sends.
 *
 * With no timezone on the row these fall back to the viewer's zone — the same fallback
 * the classic makes. That is a last resort, not a default: pass the clinic's zone
 * wherever the row carries one.
 */
import moment from 'moment-timezone';

import { readPrefs } from './prefs';

const PARSE = 'YYYY-MM-DDTHH:mm:ssZ';

/* ---- the console's one way of writing a date -------------------------------------
   Every date the new console shows goes through these, so no screen invents its own:
   `Sep 4, 2026` for a day, `Sep 4, 2026 · 2:30 PM` with a time, and a range that drops
   what its two ends share — `Sep 1 – 14, 2026`, `Aug 25 – Sep 7, 2026`,
   `Dec 29, 2025 – Jan 4, 2026`. The API's `YYYY-MM-DD` strings are read as exactly that,
   so a date never shifts a day for a viewer west of UTC. */
export const DATE_FORMATS = [
  {
    value: 'medium',
    date: 'MMM D, YYYY',
    picker: 'MMM d, yyyy',
    /* A range drops what its two ends share: `Sep 1 – 14, 2026`. */
    range: (a, b) => {
      if (a.isSame(b, 'month')) return `${a.format('MMM D')} – ${b.format('D, YYYY')}`;
      if (a.isSame(b, 'year')) return `${a.format('MMM D')} – ${b.format('MMM D, YYYY')}`;
      return `${a.format('MMM D, YYYY')} – ${b.format('MMM D, YYYY')}`;
    }
  },
  {
    value: 'dayFirst',
    date: 'D MMM YYYY',
    picker: 'd MMM yyyy',
    range: (a, b) => {
      if (a.isSame(b, 'month')) return `${a.format('D')} – ${b.format('D MMM YYYY')}`;
      if (a.isSame(b, 'year')) return `${a.format('D MMM')} – ${b.format('D MMM YYYY')}`;
      return `${a.format('D MMM YYYY')} – ${b.format('D MMM YYYY')}`;
    }
  },
  {
    value: 'iso',
    date: 'YYYY-MM-DD',
    picker: 'yyyy-MM-dd',
    /* Both ends in full: the point of this one is that every date is the same
       width and sorts as text, which abbreviating would undo. */
    range: (a, b) => `${a.format('YYYY-MM-DD')} – ${b.format('YYYY-MM-DD')}`
  },
  {
    /* The classic console already writes dates this way in a few places (the
       appointments scheduler, the reviews widget) — offered here rather than left
       as one more style nobody chose. */
    value: 'usSlash',
    date: 'MM/DD/YYYY',
    picker: 'MM/dd/yyyy',
    /* Not abbreviated like the word formats above: a slash date reads as one
       block, and dropping a field out of MM/DD/YYYY leaves something that looks
       like a different date rather than a shortened one. */
    range: (a, b) => `${a.format('MM/DD/YYYY')} – ${b.format('MM/DD/YYYY')}`
  },
  {
    value: 'ukSlash',
    date: 'DD/MM/YYYY',
    picker: 'dd/MM/yyyy',
    range: (a, b) => `${a.format('DD/MM/YYYY')} – ${b.format('DD/MM/YYYY')}`
  },
  /* From here down, the same three styles again with the year cut to two digits — a
     table of many rows, all from within a few years of each other, gets more of its
     width back from this than from any of the full-year styles above. */
  {
    value: 'mediumShort',
    date: 'MMM D, YY',
    picker: 'MMM d, yy',
    range: (a, b) => {
      if (a.isSame(b, 'month')) return `${a.format('MMM D')} – ${b.format('D, YY')}`;
      if (a.isSame(b, 'year')) return `${a.format('MMM D')} – ${b.format('MMM D, YY')}`;
      return `${a.format('MMM D, YY')} – ${b.format('MMM D, YY')}`;
    }
  },
  {
    value: 'usSlashShort',
    date: 'MM/DD/YY',
    picker: 'MM/dd/yy',
    range: (a, b) => `${a.format('MM/DD/YY')} – ${b.format('MM/DD/YY')}`
  },
  {
    value: 'ukSlashShort',
    date: 'DD/MM/YY',
    picker: 'dd/MM/yy',
    range: (a, b) => `${a.format('DD/MM/YY')} – ${b.format('DD/MM/YY')}`
  }
];

/* Read once and kept, because these are called for every cell of a long table — and
   `localStorage` plus a JSON.parse per cell is a real cost. Settings announces a
   change with `nt:prefs`, which is what puts the console on the new format without a
   reload. */
let chosen = null;
const format = () => {
  if (!chosen) {
    const want = readPrefs().dateFormat;
    chosen = DATE_FORMATS.find((f) => f.value === want) || DATE_FORMATS[0];
  }
  return chosen;
};

if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('nt:prefs', (e) => {
    const want = (e.detail || {}).dateFormat;
    chosen = DATE_FORMATS.find((f) => f.value === want) || DATE_FORMATS[0];
  });
}

/** Today in each style, so the setting shows what it will do rather than naming it. */
export const dateFormatChoices = () => DATE_FORMATS.map((f) => ({
  value: f.value,
  label: moment().format(f.date)
}));

/** The tokens the rest of this file formats with, and the one the picker needs. */
const DATE_FORMAT_OF = () => format().date;
const DATETIME_FORMAT_OF = () => `${format().date} · h:mm A`;
export const pickerFormat = () => format().picker;

const asMoment = (d) => {
  if (d == null || d === '') return null;
  if (moment.isMoment(d)) return d;
  const m = /^\d{4}-\d{2}-\d{2}$/.test(String(d)) ? moment(String(d), 'YYYY-MM-DD') : moment(d);
  return m.isValid() ? m : null;
};

export const formatDay = (d) => {
  const m = asMoment(d);
  return m ? m.format(DATE_FORMAT_OF()) : '—';
};

export const formatDateTime = (d) => {
  const m = asMoment(d);
  return m ? m.format(DATETIME_FORMAT_OF()) : '—';
};

export const formatRange = (start, end) => {
  const a = asMoment(start);
  const b = asMoment(end);
  if (!a || !b) return a || b ? formatDay(a || b) : '—';
  if (a.isSame(b, 'day')) return a.format(DATE_FORMAT_OF());
  return format().range(a, b);
};

const inZone = (str, timezone) => (timezone
  ? moment.tz(str, PARSE, timezone)
  : moment(str));

/** Date and time together, in the clinic's zone: `Mar 4, 2026 · 2:20 PM`. */
export const leadDateTime = (str, timezone) => (str ? inZone(str, timezone).format(DATETIME_FORMAT_OF()) : '—');

/**
 * The instant a lead's wall time stands for, in ms — what "how long ago" and the age
 * bands are worked out from. It needs the zone: without it a browser reads the digits as
 * its own local time, so a viewer in India saw a New York lead as nine and a half hours
 * older than it was, and its triage band a step further along.
 */
export const leadInstant = (str, timezone) => {
  if (!str) return NaN;
  const m = inZone(str, timezone);
  return m.isValid() ? m.valueOf() : NaN;
};

/* A string that names its own offset — `Z` or `+05:30` — is an instant already. */
const HAS_OFFSET = /(?:Z|[+-]\d\d:?\d\d)$/i;

/**
 * An instant sent as ISO at any precision — a booking tool's appointment start and end —
 * in the clinic's zone. Unlike the lead helpers above it converts the instant rather
 * than re-reading the wall time, because these arrive in UTC. One without an offset
 * (GoHighLevel's `start_time` comes back as a bare DATETIME) is UTC too; reading it as
 * the browser's local time put the appointment hours out by the viewer's own offset.
 */
export const instantInZone = (str, timezone, token = DATETIME_FORMAT_OF()) => {
  if (!str) return '—';
  const m = HAS_OFFSET.test(String(str)) ? moment(str) : moment.utc(str);
  if (!m.isValid()) return '—';
  return (timezone && moment.tz.zone(timezone) ? m.tz(timezone) : m).format(token);
};

/**
 * The zone's short name at that moment — `EDT`, `MST`, `PDT` — for a screen that mixes
 * clinics in different zones and has to say which clock each row is on. Empty when the
 * zone is unknown, so the caller prints nothing rather than a wrong guess.
 */
export const zoneAbbr = (str, timezone) => {
  if (!str || !timezone || !moment.tz.zone(timezone)) return '';
  return moment.tz(str, PARSE, timezone).format('z');
};

/** Time alone, in the clinic's zone: `2:20 PM`. */
export const leadTime = (str, timezone) => (str ? inZone(str, timezone).format('h:mm A') : '—');

/** Date alone, in the clinic's zone: `Mar 4, 2026`. */
export const leadDate = (str, timezone) => (str ? inZone(str, timezone).format(DATE_FORMAT_OF()) : '—');

/**
 * How long ago, phrased relatively. The sentence is the same everywhere — "2 hours ago"
 * — but the instant it is counted from must be read in the clinic's zone (leadInstant).
 */
export const leadAgo = (str, timezone) => {
  const at = leadInstant(str, timezone);
  return Number.isNaN(at) ? '' : moment(at).fromNow();
};

/**
 * For a date input, which wants `YYYY-MM-DD` in the clinic's zone — otherwise editing a
 * lead near midnight offers the wrong day and saves it.
 */
export const dateInputValue = (str, timezone) => (str ? inZone(str, timezone).format('YYYY-MM-DD') : '');

/** For a datetime-local input. */
export const dateTimeInputValue = (str, timezone) => (str ? inZone(str, timezone).format('YYYY-MM-DDTHH:mm') : '');
